import ChartWrapper from 'components/chart-wrapper/ChartWrapper';
import ContentContainer from 'components/content-container/ContentContainer';
import InfiniteTable from 'components/infinite-table/InfiniteTable';

import { detailedViewTable } from 'constants/team';
import ReactECharts from 'echarts-for-react';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    selectRmDropdownValues,
    selectMarketValue,
    selectRmValue,
    selectTeamValue,
    selectTeamViewPeerComparisonChartData,
    selectTeamViewTopFiveEmergingMajorRmsData,
    selectTeamViewTopFiveEmergingModerateRmsData,
    selectTeamViewTrendAnalysisChartData,
    selectTeamViewMinorThreshold,
    selectTeamViewMajorThreshold,
    selectTeamViewTopFiveRmsData,
    selectTeamViewTopFiveSpikeData,
    selectTimeslicerEndMonth,
    selectTimeslicerIsSameMonth,
    selectTimeslicerStartMonth,
    selectZoneValue,
    selectFiltersAllValues
} from 'selectors/selectors';
import {
    fetchPeerComparison,
    fetchTrendAnalysis,
    fetchTopAndEmergingSpikes,
    fetchScenarioStats,
    fetchTopRanks
} from 'slices/TeamSlice';
import { updateFilter, fetchFilteredDropdownValues } from 'slices/FiltersSlice';
import {
    RMCardGroupDataType,
    RMCardGroupTooltipType,
    FilterType
} from 'types/types';
import {
    generateRequestParamsForTeamViewApi,
    generateRequestParamsForTeamViewFilterApi
} from 'utils/api';
import {
    scoreDecompositionChartOptions,
    teamPeerComparisonChartOptions,
    teamTrendAnalysisChartOptions,
    marketAndTeamTop5ChartOptions
} from 'utils/charts';
import { areAllFilterValuesNumberValues } from 'utils/filters';
import RmCardsGroupSeries from 'components/rm-cards-group-series/RmCardsGroupSeries';
import {
    calculateLeftValueForGraphTooltip,
    calculateStartEndMonth,
    calculateTopValueForGraphTooltip,
    generateUpperRightDateRangeContent,
    isTypeNumber
} from 'utils/common';
import { SCENARIO_LIST } from 'constants/components';
import { useLocation } from 'react-router-dom';

import styles from './Team.module.scss';

const Team = ({ navigateTo }) => {
    const { pathname } = useLocation();
    const dispatch = useDispatch();

    // Selectors
    const tsStart = useSelector(selectTimeslicerStartMonth);
    const tsEnd = useSelector(selectTimeslicerEndMonth);
    const isSameMonth = useSelector(selectTimeslicerIsSameMonth);
    const allValues = useSelector(selectFiltersAllValues);

    const dates = calculateStartEndMonth(tsStart, tsEnd, isSameMonth);
    const nonSpikeDates = calculateStartEndMonth(
        tsStart,
        tsEnd,
        isSameMonth,
        true
    );
    const { start, end } = { ...dates };
    const { start: nonSpikeStart, end: nonSpikeEnd } = { ...nonSpikeDates };

    const zoneValue = useSelector(selectZoneValue);
    const marketValue = useSelector(selectMarketValue);
    const teamValue = useSelector(selectTeamValue);
    const rmValue = useSelector(selectRmValue);

    const topFiveRms = useSelector(selectTeamViewTopFiveRmsData);
    const topFiveRmsSpike = useSelector(selectTeamViewTopFiveSpikeData);
    const topFiveEmergingModerateRms = useSelector(
        selectTeamViewTopFiveEmergingModerateRmsData
    );
    const topFiveEmergingMajorRms = useSelector(
        selectTeamViewTopFiveEmergingMajorRmsData
    );
    const peerComparisonChartData = useSelector(
        selectTeamViewPeerComparisonChartData
    );
    const trendAnalysisChartData = useSelector(
        selectTeamViewTrendAnalysisChartData
    );
    const minorThreshold = useSelector(selectTeamViewMinorThreshold);
    const majorThreshold = useSelector(selectTeamViewMajorThreshold);
    const rmDropdownValues = useSelector(selectRmDropdownValues);

    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipData, setTooltipData] = useState([]);
    const [chartsContainerHeight, setChartsContainerHeight] = useState(null);
    const [selectedDataIndex, setSelectedDataIndex] = useState(0);
    const [barValue, setBarValue] = useState(0);
    const [highestScore, setHighestScore] = useState(0);

    useEffect(() => {
        if (tsStart && tsEnd) {
            if (
                areAllFilterValuesNumberValues({
                    startMonth: tsStart.epoch,
                    endMonth: tsEnd.epoch,
                    zone: zoneValue,
                    market: marketValue,
                    team: teamValue,
                    rm: rmValue
                })
            ) {
                const checkedMarketValue =
                    allValues.market.value === marketValue ? null : marketValue;
                const checkedTeamValue =
                    allValues.team.value === teamValue ? null : teamValue;
                const checkedRmValue =
                    allValues.rm.value === rmValue ? null : rmValue;

                // make sure to change rm values to 'ALL' value
                dispatch(
                    updateFilter({
                        value: allValues.rm.value,
                        label: 'All',
                        filterType: FilterType.RM
                    })
                );

                dispatch(
                    fetchTopRanks(
                        generateRequestParamsForTeamViewApi(
                            isTypeNumber(zoneValue) ? zoneValue : null,
                            checkedMarketValue,
                            checkedTeamValue,
                            checkedRmValue,
                            tsStart.epoch,
                            tsEnd.epoch
                        )
                    )
                );
                dispatch(
                    fetchTrendAnalysis(
                        generateRequestParamsForTeamViewApi(
                            isTypeNumber(zoneValue) ? zoneValue : null,
                            null,
                            checkedTeamValue,
                            null,
                            null,
                            tsEnd.epoch
                        )
                    )
                );
                dispatch(
                    fetchPeerComparison(
                        generateRequestParamsForTeamViewApi(
                            isTypeNumber(zoneValue) ? zoneValue : null,
                            null,
                            checkedTeamValue,
                            null,
                            tsStart.epoch,
                            tsEnd.epoch
                        )
                    )
                );
                dispatch(
                    fetchTopAndEmergingSpikes(
                        generateRequestParamsForTeamViewApi(
                            isTypeNumber(zoneValue) ? zoneValue : null,
                            checkedMarketValue,
                            checkedTeamValue,
                            checkedRmValue,
                            tsStart.epoch,
                            tsEnd.epoch
                        )
                    )
                );
                dispatch(
                    fetchScenarioStats(
                        generateRequestParamsForTeamViewApi(
                            null,
                            marketValue,
                            teamValue,
                            rmValue,
                            tsStart.epoch,
                            tsEnd.epoch
                        )
                    )
                );
            }
        }
    }, [marketValue, teamValue, rmValue, tsStart, tsEnd]);

    useEffect(() => {
        const chartContainerElement = document.querySelector(
            'div[data-title^=charts]'
        );
        setChartsContainerHeight(chartContainerElement.offsetHeight);
    });

    const cardsDataArray = [
        {
            titleOne: 'Current Risk',
            titleTwo: 'Current Risk',
            subTitleOne: '- By Total Risk Score',
            subTitleTwo: '- By Highest Spike',
            upperRightContentOne: generateUpperRightDateRangeContent({
                tsStart,
                tsEnd,
                nonSpikeStart,
                nonSpikeEnd,
                start,
                end,
                shouldShowSameWhenOneMonth: true
            }),
            upperRightContentTwo: generateUpperRightDateRangeContent({
                tsStart,
                tsEnd,
                nonSpikeStart,
                nonSpikeEnd,
                start,
                end,
                isSpike: true
            }),
            footerOne: 'Total Risk Score',
            footerTwo: ['Total Risk Score', '(Spike)'],
            groupOneData: topFiveRms,
            groupTwoData: topFiveRmsSpike,
            groupOneDataType: RMCardGroupDataType.TopFiveRms,
            groupTwoDataType: RMCardGroupDataType.TopFiveRmsSpike,
            groupOneTooltipType: RMCardGroupTooltipType.ScoreDecomposition,
            groupTwoTooltipType: RMCardGroupTooltipType.TrendAnalysis,
            isBottomGroup: false
        },
        {
            titleOne: 'Emerging Risk',
            titleTwo: 'Emerging Risk',
            subTitleOne: '- Red Threshold Breach Prediction',
            subTitleTwo: '- Amber Threshold Breach Prediction',
            upperRightContentOne: generateUpperRightDateRangeContent({
                tsStart,
                tsEnd,
                nonSpikeStart,
                nonSpikeEnd,
                start,
                end,
                isSpike: true
            }),
            upperRightContentTwo: generateUpperRightDateRangeContent({
                tsStart,
                tsEnd,
                nonSpikeStart,
                nonSpikeEnd,
                start,
                end,
                isSpike: true
            }),
            footerOne: 'Total Risk Score',
            footerTwo: 'Total Risk Score',
            groupOneData: topFiveEmergingMajorRms,
            groupTwoData: topFiveEmergingModerateRms,
            groupOneDataType: RMCardGroupDataType.TopFiveEmergingMajorRm,
            groupTwoDataType: RMCardGroupDataType.TopFiveEmergingModerateRm,
            groupOneTooltipType: RMCardGroupTooltipType.Default,
            groupTwoTooltipType: RMCardGroupTooltipType.Default,
            isBottomGroup: true
        }
    ];

    const onChartNodeMouseOver = (param) => {
        const { data, name, dataIndex } = param;
        const dataToUse = topFiveRms;
        const dataToPassToTooltip = dataToUse.find(
            (data) => data.dimension === name
        );
        setBarValue(data);
        setTooltipData(dataToPassToTooltip);
        setSelectedDataIndex(dataIndex);
        setHighestScore(Math.max(...dataToUse.map((d) => d.score)));
        setShowTooltip(true);
    };

    const onChartNodeMouseOut = () => {
        setShowTooltip(false);
    };

    const onClickTop5 = (param) => {
        const rm = rmDropdownValues.find((r) => r.label === param.name);

        dispatch(
            updateFilter({
                value: rm.value,
                label: rm.label,
                filterType: FilterType.RM
            })
        );

        const requestParamsObj = generateRequestParamsForTeamViewFilterApi(
            tsStart ? tsStart.epoch : null,
            tsEnd ? tsEnd.epoch : null,
            zoneValue,
            rm.value
        );
        const enrichedParamsObj = {
            requestParams: {
                ...requestParamsObj.requestParams,
                selectedFilterType: FilterType.RM,
                label: rm.label,
                value: rm.value
            }
        };

        dispatch(fetchFilteredDropdownValues(enrichedParamsObj));

        navigateTo(pathname, '/rm');
    };

    const onEvents = {
        mouseover: onChartNodeMouseOver,
        mouseout: onChartNodeMouseOut,
        click: onClickTop5
    };

    const onClickPeerComparison = (param) => {
        const { name, seriesIndex } = param;
        SCENARIO_LIST.map((scenario) => {
            if (name === scenario.name && seriesIndex === 0)
                navigateTo(pathname, scenario.path);
        });
    };
    const onPeerComparisonEvents = {
        click: onClickPeerComparison
    };

    const memoizedRmTop5Chart = useMemo(() => {
        return (
            <ReactECharts
                option={marketAndTeamTop5ChartOptions(
                    topFiveRms,
                    'Top 5 RMs - By Total Risk Score',
                    'team'
                )}
                style={{ width: '100%', height: '100%' }}
                onEvents={onEvents}
            ></ReactECharts>
        );
    }, [topFiveRms]);

    const chartRef = useRef(null);
    useEffect(() => {
        chartRef.current.getEchartsInstance().dispose();
        chartRef.current
            .getEchartsInstance()
            .setOption(teamTrendAnalysisChartOptions(trendAnalysisChartData));
    }, [trendAnalysisChartData]);

    const memoizedPeerComparisonChart = useMemo(() => {
        return (
            <ReactECharts
                option={teamPeerComparisonChartOptions(peerComparisonChartData)}
                style={{ width: '100%', height: '100%' }}
                onEvents={onPeerComparisonEvents}
            ></ReactECharts>
        );
    }, [peerComparisonChartData]);

    const isTooltipContainsFiveBar =
        tooltipData &&
        tooltipData.s1Score > 0 &&
        tooltipData.s2Score > 0 &&
        tooltipData.s3Score > 0 &&
        tooltipData.s4Score > 0 &&
        tooltipData.correlationScore > 0;

    return (
        <ContentContainer>
            <div className={styles.chartsContainer} data-title={'charts'}>
                <ChartWrapper comboClass={styles.rightMargin}>
                    {memoizedRmTop5Chart}
                </ChartWrapper>
                {showTooltip && (
                    <ChartWrapper
                        comboClass={styles.absolutePosition}
                        style={{
                            top: `${calculateTopValueForGraphTooltip(
                                topFiveRms.length,
                                chartsContainerHeight,
                                selectedDataIndex
                            )}px`,
                            left: `${calculateLeftValueForGraphTooltip(
                                barValue,
                                highestScore
                            )}%`
                        }}
                        width={'320px'}
                        height={'auto'}
                    >
                        <ReactECharts
                            option={scoreDecompositionChartOptions(tooltipData)}
                            style={
                                isTooltipContainsFiveBar
                                    ? { width: '300px', height: '330px' }
                                    : { width: '300px' }
                            }
                        ></ReactECharts>
                    </ChartWrapper>
                )}

                <ChartWrapper comboClass={styles.rightMargin}>
                    <ReactECharts
                        option={teamTrendAnalysisChartOptions(
                            trendAnalysisChartData
                        )}
                        style={{ width: '100%', height: '100%' }}
                        ref={chartRef}
                    ></ReactECharts>
                </ChartWrapper>

                <ChartWrapper>{memoizedPeerComparisonChart}</ChartWrapper>
            </div>

            <RmCardsGroupSeries
                seriesTitle={'Top 5 RMs'}
                minorThreshold={minorThreshold}
                majorThreshold={majorThreshold}
                cardsDataArray={cardsDataArray}
                navigateTo={navigateTo}
            />

            <div className={styles.tableContainer}>
                <div className={styles.tableHeader}>
                    <div className={styles.title}>Detailed View</div>
                </div>
                <InfiniteTable
                    zone={zoneValue}
                    market={marketValue}
                    team={teamValue}
                    rm={rmValue}
                    start={tsStart}
                    end={tsEnd}
                    allValues={allValues}
                    columns={detailedViewTable}
                />
            </div>
        </ContentContainer>
    );
};

Team.propTypes = {
    navigateTo: PropTypes.func
};
export default Team;
